import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Input, Row, App, Typography } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { InputNumber } from 'antd';
import { Switch } from 'antd';
import { ColorPicker } from 'antd';
import usePermissions from '../../hooks/usePermissions';
import { checkPlanAddonExists, createPlanAddon, getPlanAddonByIdAdmin, updatePlanAddon } from '../../services/plan-addon';

const { Title } = Typography;

const { useApp } = App;

const PlanAddonUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();

  const { canAny } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [planAddon, setPlanAddon] = useState([]);


  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getPlanAddonByIdAdmin(id)
      .then((plan) => {
        form.setFieldsValue({
          code: plan.code?.trim(),
          name: plan.name?.trim(),
          description: plan.description?.trim(),
          extraVideos: Number(plan.extraVideos),
          price: Number(plan.price),
          order: Number(plan.order),
          currency: plan.currency?.trim(),
          isActive: plan.isActive || false,
          color: plan.color || '#BA955E',
        });
        setPlanAddon(plan);
      })
      .catch(() => {
        message.error('No se pudo cargarl paquete de video');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      const exists = await checkPlanAddonExists(values.code.trim(),id);
      if(exists === true){
        message.warning('Ya existe un paquete de videos con el código proporcionado');
        return;
      }

        const payload = {
            code: values.code?.trim(),
            name: values.name?.trim(),
            description: values.description?.trim(),
            extraVideos: values.extraVideos || 0,
            price: values.price || 0,
            currency: values.currency?.trim(),
            isActive: values.isActive,
            order: values.order || 0,
            color: typeof values.color === 'string' ? values.color : (values.color?.toHexString?.() || values.color),
        };

      if (isEdit && id) {
        await updatePlanAddon(id, payload);
        message.success('Paquete de Video actualizado correctamente');
      } else {
        await createPlanAddon(payload);
        message.success('Paquete de Video agregado');
      }
      navigate('/video-packages/all')
    } catch (err) {
      // Extraer datos del error
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al guardar el usuario';
      
      // Si es un error de validación (422), mostrar errores en los campos del formulario
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        console.log('Issues encontrados:', issues);
        
        if (issues.length > 0) {
          // Limpiar errores previos
          form.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message) 
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              console.log('Mapeando error para campo:', fieldPath, 'Mensaje:', issue.message);
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          console.log('Field errors a establecer:', fieldErrors);
          
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
            console.log('Errores establecidos en el formulario');
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/video-packages/all');
  };

  return (
    <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button 
                type="text" 
                icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
                style={{ 
                padding: 0,
                color: '#BA955E',
                fontSize: '24px',
                fontWeight: '700',
                height: 'auto'
                }}
                onClick={handleBack}
            />
            <Title level={3} style={{ marginBottom: 0 }}>
            {isEdit ? 'Editar Paquete de Video ' + planAddon?.code : 'Agregar Paquete de Video'}
            </Title>
        </div>
        <Card loading={loading}>
            <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            autoComplete="off"
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Código"
                            name="code"
                            rules={[
                            { required: true, message: 'El código es requerido' },
                            ]}
                        >
                            <Input placeholder="BAS01" allowClear />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Paquete de Video"
                            name="name"
                            rules={[
                            { required: true, message: 'El nombre del paquete de video es requerido' },
                            ]}
                        >
                            <Input placeholder="Paquete de Video" allowClear />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={18}>
                        <Form.Item
                            label="Descripción"
                            name="description"
                            rules={[
                            { required: true, message: 'La descripción es requerida' },
                            ]}
                        >
                            <Input placeholder="Paquete de Video gratuito" allowClear />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item
                            label="Cantidad de videos"
                            name="extraVideos"
                            rules={[
                            { required: true, message: 'Cantidad de videos es requerido' },
                            { type: 'number', min: 1, message: 'No puedes ingresar un valor menor a 1', },
                            ]}
                        >
                            <InputNumber placeholder="1" min={1} style={{width: '100%'}} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={5}>
                        <Form.Item
                            label="Precio"
                            name="price"
                            rules={[
                            { required: true, message: 'El precio del paquete de video es requerido' },
                            { type: 'number', min: 0, message: 'No puedes ingresar un valor menor a 0', },
                            ]}
                        >
                            <InputNumber placeholder="199" min={0} style={{width: '100%'}} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={5}>
                        <Form.Item
                            label="Monenda"
                            name="currency"
                            rules={[
                            { required: true, message: 'La moneda del paquete de video es requerido' },
                            ]}
                        >
                            <Input placeholder="MXN" allowClear />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Item
                            label="Color"
                            name="color"
                        >
                            <ColorPicker showText />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={5}>
                        <Form.Item
                            label="Orden"
                            name="order"
                            rules={[
                            { required: false, message: 'El orden del paquete de video es requerido' },
                            { type: 'number', min: 1, message: 'No puedes ingresar un valor menor a 0', },
                            ]}
                        >
                            <InputNumber placeholder="1" min={1} style={{width: '100%'}} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={5}>
                        <Form.Item
                            label="¿Activar Paquete de Video?"
                            name="isActive"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>

                <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
                    <Col>
                    <Button onClick={() => navigate(-1)} disabled={loading}>
                        Cancelar
                    </Button>
                    </Col>
                    {canAny(['plan_addons.create', 'plan_addons.update']) && (<Col>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {isEdit ? 'Guardar cambios' : 'Guardar'}
                        </Button>
                    </Col>)}
                </Row>
            </Form>
        </Card>
    </div>
  );
};

export default PlanAddonUpsertPage;

